import React from 'react';
import { WriterProvider } from './context/WriterProvider';
import { AIProvider } from './context/AIProvider';
import { AuthProvider } from './context/AuthProvider';
import { WritingModesProvider } from './context/WritingModesContext';
import { useWriter } from './hooks/useWriter';
import { isFlagEnabled } from '@/core/flags';
import { I18nProvider } from '@/i18n/I18nProvider';
import { useI18n } from '@/i18n/useI18n';
// Modales pesados en lazy-load
const ProjectSetupModal = React.lazy(() => import('@features/project-setup/ui/ProjectSetupModal').then(m => ({ default: m.ProjectSetupModal })));
const ProjectsPage = React.lazy(() => import('@pages/ProjectsPage'));
const EditorPage = React.lazy(() => import('@pages/EditorPage'));
// Nota: AuthDebug disponible ahora en shared/ui/auth/AuthDebug (solo dev) si se vuelve a necesitar
import SyncConsole from '@shared/ui/SyncConsole.tsx';
import DebugPanel from '@shared/ui/DebugPanel.tsx';
import { SyncCenterModal } from '@features/sync/ui/SyncCenterModal';

const AppContent: React.FC = () => {
  const { t } = useI18n();
  const [, setFlagVersion] = React.useState(0);
  React.useEffect(() => {
    const onFlagUpdate = () => setFlagVersion(v => v + 1);
    window.addEventListener('wrlens:flag-changed', onFlagUpdate as EventListener);
    window.addEventListener('wrlens:flags:reset', onFlagUpdate as EventListener);
    return () => {
      window.removeEventListener('wrlens:flag-changed', onFlagUpdate as EventListener);
      window.removeEventListener('wrlens:flags:reset', onFlagUpdate as EventListener);
    };
  }, []);
  const { activeProject, isProjectSetupOpen } = useWriter();
  const showSyncConsole = isFlagEnabled('syncDiagnostics');
  const showDebugPanel = isFlagEnabled('debug.outlineTools.v1');
    
    // Mostrar ProjectsPage si no hay proyecto activo
    if (!activeProject) {
        return (
          <>
            <React.Suspense fallback={<div className="p-6">{t('app.loading')}</div>}>
            <ProjectsPage />
          </React.Suspense>
            {isProjectSetupOpen && (
              <React.Suspense fallback={<div className="p-6">{t('app.loading_modal')}</div>}>
                <ProjectSetupModal />
              </React.Suspense>
            )}
            <SyncCenterModal />
          </>
        );
    }

    return (
      <>
        <React.Suspense fallback={<div className="p-6">{t('app.loading_editor')}</div>}>
          <EditorPage />
        </React.Suspense>
        {/* Modales y utilidades globales */}
        {isProjectSetupOpen && (
          <React.Suspense fallback={<div className="p-6">{t('app.loading_modal')}</div>}>
            <ProjectSetupModal />
          </React.Suspense>
        )}
        {showSyncConsole ? <SyncConsole /> : null}
        {showDebugPanel ? <DebugPanel /> : null}
        <SyncCenterModal />
      </>
    );
}

const App: React.FC = () => {
  return (
    <I18nProvider>
      <AuthProvider>
        <WriterProvider>
          <AIProvider>
            <WritingModesProvider>
              <AppContent />
            </WritingModesProvider>
          </AIProvider>
        </WriterProvider>
      </AuthProvider>
    </I18nProvider>
  );
};

export default App;
